import { type HTMLAttributes } from 'react';
import cn from 'clsx';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { type CountryCode, type EnergyClass } from '@ev-tech/search-modules-api';

import IndicatorIcon from './IndicatorIcon.svg';
import FlagEndIcon from './FlagEndIcon.svg';
import { EnergyClassLabelMap, getCountryColorMap } from '../../consts/countryData';

import styles from './EnergyEfficiency.module.css';

export type EnergyEfficiencyProps = HTMLAttributes<HTMLDivElement> & {
  energyClass: EnergyClass;
  countryCode: CountryCode;
  variant?: 'basic' | 'full';
};

const CHARACTER_WIDTH = 12;
const PADDING = 16;

export const EnergyEfficiency = ({ energyClass: propertyEnergyClass, countryCode, variant = 'full', ...props }: EnergyEfficiencyProps) => {
  const energyClassItems = getCountryColorMap(countryCode);
  const { className, style, ...restProps } = props;

  const firstEnergyClass = energyClassItems[0]?.[0];
  const baseWidth = (firstEnergyClass ? EnergyClassLabelMap[firstEnergyClass].length : 1) * CHARACTER_WIDTH + PADDING;

  if (variant === 'basic') {
    const item = energyClassItems.find(([energyClass]) => energyClass === propertyEnergyClass);
    if (!item) return null;
    const [energyClass, color] = item;
    return (
      <div
        {...restProps}
        className={cn(styles.flag, className)}
        style={{ '--energy-efficiency-color': color, '--energy-efficiency-width': `unset` } as React.CSSProperties}
      >
        <Copy text={EnergyClassLabelMap[energyClass]} size="m" bold />
        <FlagEndIcon className={styles.flagEndIcon} />
      </div>
    );
  }

  return (
    <div {...restProps} className={cn(styles.container, className)} style={style}>
      {energyClassItems.map(([energyClass, color], index) => (
        <div
          key={energyClass}
          className={styles.flag}
          style={{ '--energy-efficiency-color': color, '--energy-efficiency-width': `${baseWidth + index * 12}px` } as React.CSSProperties}
        >
          <Copy text={EnergyClassLabelMap[energyClass]} size="m" bold />
          <FlagEndIcon className={styles.flagEndIcon} />
          {propertyEnergyClass === energyClass && <IndicatorIcon className={styles.indicatorIcon} />}
        </div>
      ))}
    </div>
  );
};
